import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActorDisplayName } from '../../utils/roleUtils';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { useToast } from '../../hooks/use-toast';
import {
    getGuests,
    addGuestToDepartment,
    removeGuestDepartment,
} from '../../services/api/organizationService';
import { getAvailableRoles, type RoleOption } from '../../services/api/onboardingService';
import PERMISSION_KEYS from '../../constants/permission-keys';

interface Guest {
    name?: string;
    email: string;
    role: string;
    permissions: string[];
    addedOn?: string;
}

function useGuestPermissions() {
    const { t } = useTranslation();
    return [
        { key: PERMISSION_KEYS.approve, label: t('facilities.guests.guestPermissions.approve') },
        { key: PERMISSION_KEYS.manageProtocols, label: t('facilities.guests.guestPermissions.manageProtocols') },
        { key: PERMISSION_KEYS.manageGroups, label: t('facilities.guests.guestPermissions.manageGroups') },
    ] as const;
}

export default function DepartmentGuestsSection() {
    const { toast } = useToast();
    const { t } = useTranslation();
    const guestPermissions = useGuestPermissions();
    const [guests, setGuests] = useState<Guest[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<Guest | null>(null);
    const [saving, setSaving] = useState(false);

    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    const [permissions, setPermissions] = useState<string[]>([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [guestData, roleData] = await Promise.all([
                    getGuests(),
                    getAvailableRoles(),
                ]);
                setGuests(Array.isArray(guestData) ? guestData : []);
                setRoles(Array.isArray(roleData) ? roleData : []);
            } catch {
                toast({
                    title: t('facilities.guests.toasts.loadFailed'),
                    variant: 'destructive',
                });
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetForm = () => {
        setEmail('');
        setRole('');
        setPermissions([]);
    };

    const handleAdd = async () => {
        if (!email || !role) return;
        setSaving(true);
        try {
            await addGuestToDepartment(email, role, permissions);
            const updated = await getGuests();
            setGuests(Array.isArray(updated) ? updated : []);
            setDialogOpen(false);
            resetForm();
            toast({ title: t('facilities.guests.toasts.guestAdded') });
        } catch {
            toast({
                title: t('facilities.guests.toasts.addFailed'),
                description: t('facilities.guests.toasts.addFailedDesc'),
                variant: 'destructive',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        try {
            await removeGuestDepartment(removeTarget.email);
            setGuests((prev) => prev.filter((g) => g.email !== removeTarget.email));
            setRemoveTarget(null);
            toast({ title: t('facilities.guests.toasts.guestRemoved') });
        } catch {
            toast({
                title: t('facilities.guests.toasts.removeFailed'),
                variant: 'destructive',
            });
        }
    };

    const togglePermission = (key: string) => {
        setPermissions((prev) =>
            prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
        );
    };

    return (
        <section className="mt-10">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold text-foreground">
                            {t('facilities.guests.title')}
                        </h3>
                        <span className="rounded-full bg-zinc-300 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-800">
                            {guests.length}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('facilities.guests.description')}
                    </p>
                </div>
                <Button
                    size="sm"
                    onClick={() => {
                        resetForm();
                        setDialogOpen(true);
                    }}
                >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {t('facilities.guests.addGuest')}
                </Button>
            </div>

            {guests.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('facilities.guests.emptyState')}
                </p>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('facilities.guests.name')}</TableHead>
                            <TableHead>{t('facilities.guests.email')}</TableHead>
                            <TableHead>{t('facilities.guests.role')}</TableHead>
                            <TableHead>{t('facilities.guests.permissions')}</TableHead>
                            <TableHead>{t('facilities.guests.added')}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {guests.map((guest) => (
                            <TableRow key={guest.email}>
                                <TableCell className="text-foreground">
                                    {guest.name || '—'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {guest.email}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {guest.role || '—'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {guest.permissions?.length
                                        ? guest.permissions.join(', ')
                                        : '—'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {guest.addedOn || '—'}
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-muted-foreground hover:text-destructive"
                                        onClick={() =>
                                            setRemoveTarget(guest)
                                        }
                                    >
                                        <Trash2
                                            className="h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('facilities.guests.dialogTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="guest-email">{t('facilities.guests.emailLabel')}</Label>
                            <Input
                                id="guest-email"
                                type="email"
                                placeholder={t('facilities.guests.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="guest-role">{t('facilities.guests.roleLabel')}</Label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger id="guest-role">
                                    <SelectValue placeholder={t('facilities.guests.selectRole')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.map((r) => (
                                        <SelectItem key={r._id} value={r.role}>
                                            {getActorDisplayName(r)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>{t('facilities.guests.permissionsLabel')}</Label>
                            {guestPermissions.map((perm) => (
                                <div
                                    key={perm.key}
                                    className="flex items-center gap-2"
                                >
                                    <Checkbox
                                        id={`perm-${perm.key}`}
                                        checked={permissions.includes(perm.key)}
                                        onCheckedChange={() =>
                                            togglePermission(perm.key)
                                        }
                                    />
                                    <Label
                                        htmlFor={`perm-${perm.key}`}
                                        className="text-sm font-normal"
                                    >
                                        {perm.label}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleAdd}
                            disabled={!email || !role || saving}
                        >
                            {saving ? t('facilities.guests.adding') : t('facilities.guests.addGuest')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={Boolean(removeTarget)}
                onOpenChange={(open) => {
                    if (!open) setRemoveTarget(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('facilities.guests.removeTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('facilities.guests.removeDescription', { email: removeTarget?.email })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('facilities.guests.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRemove}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('facilities.guests.remove')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
